import React, { useEffect, useState } from "react";
import { v2_all_user } from "../../actions/v2/user";

const normalizeTime = (value) => value ? String(value).slice(0, 5) : '';
const createRow = (item = {}) => {
    const values = {
        user: item.USER_ID ?? '', dateAttribute: item.DATE_ATTRIBUTE ?? '',
        from: normalizeTime(item.TIME_FROM), to: normalizeTime(item.TIME_TO),
        actualAttendance: item.ACTUAL_ATTENDANCE ?? '', ot: item.OT ?? '', remark: item.REMARK ?? '',
    };
    const row = Object.fromEntries(Object.entries(values).map(([name, value]) => [name, {
        name, value, isRequired: ['user', 'dateAttribute', 'from', 'to'].includes(name), error: '',
    }]));
    row.user.fullName = item.FULL_NAME;
    return row;
};
const emptyBulkValues = { dateAttribute: '', from: '', to: '', actualAttendance: '' };

const updateRow = (row, field, value) => {
    const updated = { ...row, [field]: { ...row[field], value } };
    if (field === 'from' || field === 'to') {
        const from = updated.from.value;
        const to = updated.to.value;
        let hours = '';
        if (from && to) {
            const toMinutes = (time) => {
                const [hour, minute] = time.split(':').map(Number);
                return hour * 60 + minute;
            };
            let minutes = toMinutes(to) - toMinutes(from);
            if (minutes < 0) minutes += 24 * 60;
            hours = Number((minutes / 60).toFixed(2));
        }
        updated.actualAttendance = { ...row.actualAttendance, value: hours };
    }
    return updated;
};

const Form = ({ onSubmit, data, buttonName = 'Submit', disabled = false, isEdit = false }) => {

    const [state, setState] = useState(() => [createRow()]);
    const [users, setUsers] = useState([]);
    const [applyToAll, setApplyToAll] = useState(emptyBulkValues);

    useEffect(() => {
        setState(data?.length ? data.map(createRow) : [createRow()]);
        setApplyToAll(emptyBulkValues);
    }, [data]);

    useEffect(() => {
        v2_all_user().then((res) => {
            if (res.status) {
                setUsers(res.result);
            }
        });
    }, []);

    const handleChange = (rowIndex, field, value) => {
        setState((current) => current.map((row, index) => index === rowIndex ? updateRow(row, field, value) : row));
    };

    const addRow = () => {
        setState((current) => [...current, createRow()]);
    };

    const deleteRow = (rowIndex) => {
        const updated = state.filter((_, idx) => idx !== rowIndex);
        setState(updated);
    };

    const handleApplyToAll = (field, value) => {
        setApplyToAll((current) => ({ ...current, [field]: value }));
        setState((current) => current.map((row) => updateRow(row, field, value)));
    };

    const handleSave = () => {
        if (disabled) return;
        const formattedData = state.map((row) => ({
            user: row.user.value,
            dateAttribute: row.dateAttribute.value,
            from: row.from.value,
            to: row.to.value,
            actualAttendance: row.actualAttendance.value,
            ot: row.ot.value,
            remark: row.remark.value,
        }));

        onSubmit(formattedData);
    };

    return (
        <fieldset className="row" disabled={disabled}>
            <div className="col-md-12">
                <div className="appraisal-report-table-responsive">
                    <table className="assessment-table" cellSpacing={0}>
                        <tbody>
                            <tr className="bghed">
                                <td>S.No.</td>
                                <td style={{ minWidth: '250px' }}>Name</td>
                                <td>Date Attribute</td>
                                <td>From</td>
                                <td>To</td>
                                <td>Actual Attendance</td>
                                <td>OT</td>
                                <td>Remark</td>
                                <td>Action</td>
                            </tr>

                            {/* Apply to All Row */}
                            <tr style={{ background: "#f5f5f5" }}>
                                <td colSpan={2}><b>Apply To All</b></td>
                                <td>
                                    <select
                                        className="form-control"
                                        value={applyToAll.dateAttribute}
                                        onChange={(e) => handleApplyToAll("dateAttribute", e.target.value)}
                                    >
                                        <option value="">Select</option>
                                        <option value="working_day">Working Day</option>
                                        <option value="leave">Leave</option>
                                        <option value="holiday">Holiday</option>
                                        <option value="weekend_off">Weekend Off</option>
                                    </select>
                                </td>
                                <td>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={applyToAll.from}
                                        aria-label="Apply to all: From"
                                        onChange={(e) => handleApplyToAll("from", e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={applyToAll.to}
                                        aria-label="Apply to all: To"
                                        onChange={(e) => handleApplyToAll("to", e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        step='0.1'
                                        className="form-control"
                                        value={applyToAll.actualAttendance}
                                        onChange={(e) => handleApplyToAll("actualAttendance", e.target.value)}
                                    />
                                </td>
                                <td colSpan={3}></td>
                            </tr>

                            {state.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    <td className="text-center">{rowIndex + 1}</td>

                                    {/* Name dropdown */}
                                    <td>
                                        {isEdit ? (
                                            row.user.fullName || users.find((user) => Number(user.USER_ID) === Number(row.user.value))?.FULL_NAME || `User #${row.user.value}`
                                        ) : <select
                                            className="form-control"
                                            value={row.user.value}
                                            onChange={(e) => handleChange(rowIndex, "user", e.target.value)}
                                        >
                                            <option value="">Select User</option>
                                            {users.map((u) => (
                                                <option key={u.USER_ID} value={u.USER_ID}>
                                                    {u.FULL_NAME}
                                                </option>
                                            ))}
                                        </select>}
                                    </td>

                                    {/* Date Attribute */}
                                    <td>
                                        <select
                                            className="form-control"
                                            value={row.dateAttribute.value}
                                            onChange={(e) => handleChange(rowIndex, "dateAttribute", e.target.value)}
                                        >
                                            <option value="">Select</option>
                                            <option value="working_day">Working Day</option>
                                            <option value="leave">Leave</option>
                                            <option value="holiday">Holiday</option>
                                            <option value="weekend_off">Weekend Off</option>
                                        </select>
                                    </td>

                                    {/* From */}
                                    <td>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={row.from.value}
                                            aria-label="From"
                                            onChange={(e) => handleChange(rowIndex, "from", e.target.value)}
                                        />
                                    </td>

                                    {/* To */}
                                    <td>
                                        <input
                                            type="time"
                                            className="form-control"
                                            value={row.to.value}
                                            aria-label="To"
                                            onChange={(e) => handleChange(rowIndex, "to", e.target.value)}
                                        />
                                    </td>

                                    {/* Actual Attendance */}
                                    <td>
                                        <input
                                            type="number"
                                            step='0.1'
                                            className="form-control"
                                            value={row.actualAttendance.value}
                                            onChange={(e) =>
                                                handleChange(rowIndex, "actualAttendance", e.target.value)
                                            }
                                        />
                                    </td>

                                    {/* OT */}
                                    <td>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={row.ot.value}
                                            onChange={(e) => handleChange(rowIndex, "ot", e.target.value)}
                                        />
                                    </td>

                                    {/* Remark */}
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={row.remark.value}
                                            onChange={(e) => handleChange(rowIndex, "remark", e.target.value)}
                                        />
                                    </td>

                                    {/* Delete Button */}
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteRow(rowIndex)}
                                        >
                                            X
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!isEdit && <tr className="addmoreitem">
                                <td colSpan={9}>
                                    <button type="button" className="btn btn-link" onClick={addRow}>
                                        + Add New User
                                    </button>
                                </td>
                            </tr>}
                        </tbody>
                    </table>
                </div>

                <button
                    type="button"
                    disabled={disabled}
                    className="btn btn-primary mt-3"
                    onClick={handleSave}
                    style={{
                        backgroundImage:
                            "linear-gradient(90deg, rgb(105, 150, 181), rgb(43, 90, 126))",
                        borderColor: "rgb(43, 90, 126)"
                    }}
                >
                    {buttonName}
                </button>
            </div>
        </fieldset>
    );
};

export default Form;
